// Level-up notification utilities
import { updateUserPoints, getUserPoints } from "lib/purchaseUtils"
import { checkForLevelUp } from "./xpUtils"

/**
 * Update user points with level-up detection and notification
 * @param {string} userId - Firebase user ID
 * @param {number} newPoints - New points value to set
 * @param {function} enqueueSnackbar - Notistack snackbar function
 * @param {string} userName - Optional user name for notifications
 * @returns {Promise<Object>} - Returns level-up information
 */
export const updateUserPointsWithLevelUpDetection = async (
  userId,
  newPoints,
  enqueueSnackbar,
  userName = "User"
) => {
  try {
    // Get current points before updating
    const oldPoints = await getUserPoints(userId)

    // Update the points
    await updateUserPoints(userId, newPoints)

    // Check for level up
    const levelUpInfo = checkForLevelUp(oldPoints, newPoints)

    // Show level-up notification if applicable
    if (levelUpInfo.leveledUp) {
      const message =
        levelUpInfo.levelsGained === 1
          ? `🎉 ${userName} leveled up to Level ${levelUpInfo.newLevel}! Congratulations! 🎉`
          : `🎉 ${userName} leveled up ${levelUpInfo.levelsGained} levels to Level ${levelUpInfo.newLevel}! Amazing! 🎉`

      enqueueSnackbar(message, {
        variant: "success",
        autoHideDuration: 6000,
        anchorOrigin: {
          vertical: "top",
          horizontal: "center",
        },
      })
    }

    return levelUpInfo
  } catch (error) {
    throw error
  }
}

/**
 * Create a level-up celebration notification for manual use
 * @param {function} enqueueSnackbar - Notistack snackbar function
 * @param {number} newLevel - The level the user reached
 * @param {string} userName - User name for the notification
 * @param {number} levelsGained - Number of levels gained (default 1)
 */
export const showLevelUpNotification = (
  enqueueSnackbar,
  newLevel,
  userName = "User",
  levelsGained = 1
) => {
  const message =
    levelsGained === 1
      ? `🎉 ${userName} leveled up to Level ${newLevel}! Congratulations! 🎉`
      : `🎉 ${userName} leveled up ${levelsGained} levels to Level ${newLevel}! Amazing! 🎉`

  enqueueSnackbar(message, {
    variant: "success",
    autoHideDuration: 6000,
    anchorOrigin: {
      vertical: "top",
      horizontal: "center",
    },
  })
}
